import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

// Swapping the theme repaints every color token at once. Any element with a
// color transition would smear through an intermediate palette on the way
// over, so transitions are frozen for the frame the swap takes (see the
// `html.theme-switching` rule in index.css) and released again right after.
// This replaces the old blanket `.dark * { transition: none }`, which cost
// dark mode every animation in the app to fix a one-frame problem.
let releaseFrame: number | null = null

export function freezeTransitionsForThemeSwap() {
  const root = window.document.documentElement

  root.classList.add('theme-switching')
  // Read a layout property to flush the frozen state synchronously, so the
  // new colors are committed while the freeze is still in effect.
  void root.offsetHeight

  if (releaseFrame !== null) cancelAnimationFrame(releaseFrame)
  releaseFrame = requestAnimationFrame(() => {
    releaseFrame = null
    root.classList.remove('theme-switching')
  })
}

function applyThemeClass(resolvedTheme: 'dark' | 'light') {
  const root = window.document.documentElement

  root.classList.remove('dark', 'light')
  root.classList.add(resolvedTheme)
  freezeTransitionsForThemeSwap()
}

function resolveTheme(theme: Theme): 'dark' | 'light' {
  if (theme !== 'system') return theme
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

// Custom event name for theme sync
const THEME_CHANGE_EVENT = 'scrambl-theme-change'

// Helper to dispatch theme change event (called from useThemePreferences)
export function dispatchThemeChange(theme: Theme) {
  // useThemePreferences flips the `dark`/`light` class itself and then calls
  // this, so freeze here too — the swap has not been painted yet at this
  // point, and the ThemeProvider effect that follows freezes again harmlessly.
  freezeTransitionsForThemeSwap()
  window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: theme }))
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'scrambl-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check for stored theme preference
    const storedTheme = localStorage.getItem(storageKey)
    return (storedTheme as Theme) || defaultTheme
  })

  // Listen for theme changes from useThemePreferences
  useEffect(() => {
    const handleThemeChange = (event: CustomEvent<Theme>) => {
      setTheme(event.detail)
    }

    window.addEventListener(
      THEME_CHANGE_EVENT,
      handleThemeChange as EventListener,
    )
    return () =>
      window.removeEventListener(
        THEME_CHANGE_EVENT,
        handleThemeChange as EventListener,
      )
  }, [])

  useEffect(() => {
    applyThemeClass(resolveTheme(theme))
  }, [theme])

  useEffect(() => {
    // Listen for system theme changes when theme is set to "system"
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      if (theme === 'system') {
        applyThemeClass(mediaQuery.matches ? 'dark' : 'light')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      setTheme(newTheme)
      localStorage.setItem(storageKey, newTheme)
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
